import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { rafraichirTelephone } from '@/api/voice'

export type NumberCountry = 'CA' | 'US'
export type NumberType = 'local' | 'tollfree' | 'mobile'

export interface AvailableNumber {
  phoneNumber: string
  friendlyName: string
  locality: string | null
  region: string | null
  isoCountry: NumberCountry
}

export interface SearchAvailableNumbersInput {
  country: NumberCountry
  type: NumberType
  areaCode?: string
  region?: string
  contains?: string
  limit?: number
}

interface AvailableNumbersResponse {
  ok: boolean
  data: AvailableNumber[]
}

/** GET /api/numbers/available/ — recherche numéros Twilio disponibles. */
export function useSearchAvailableNumbers(
  input: SearchAvailableNumbersInput,
  enabled = true,
) {
  return useQuery({
    queryKey: ['twilio-available-numbers', input],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('country', input.country)
      params.set('type', input.type)
      params.set('limit', String(input.limit ?? 10))
      if (input.areaCode) params.set('areaCode', input.areaCode)
      if (input.region) params.set('region', input.region)
      if (input.contains) params.set('contains', input.contains)

      const { data } = await api.get<AvailableNumbersResponse>(
        `/api/numbers/available/?${params.toString()}`,
      )
      return data.data ?? []
    },
    enabled,
    staleTime: 30_000,
  })
}

export interface PurchaseNumberInput {
  phoneNumber: string
  companyId: number
}

export interface PurchaseNumberResponse {
  ok: boolean
  sid: string
  phoneNumber: string
  emergency_address_sid: string | null
  emergency_address_status: string | null
}

/** POST /api/numbers/purchase/ — achète un numéro Twilio. */
export function usePurchaseNumber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: PurchaseNumberInput) => {
      const { data } = await api.post<PurchaseNumberResponse>(
        '/api/numbers/purchase/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
      queryClient.invalidateQueries({ queryKey: ['company'] })
      rafraichirTelephone(queryClient)
    },
  })
}
